import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Search, Loader2, Save, Package, Calculator } from 'lucide-react';
import { bomAPI, productAPI } from '../../services/api';
import ProductSearchInput from '../ui/ProductSearchInput';

const emptyItem = { seq: '', item_id: '', item_description: '', quantity: 0, unit: 'Kg', rate: 0, total_cost: 0, is_primary: true, alternates: [] };

const BOMCreateForm = ({ onBack, onCreated }) => {
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    bom_number: '',
    bom_name: '',
    fg_item_id: '',
    fg_uom: 'PCS',
    items: [{ ...emptyItem, seq: '1' }],
    child_bom_other_cost: 0,
    fg_other_cost: 0,
    total_fg_cost: 0,
    remarks: '',
  });

  useEffect(() => {
    setLoading(true);
    productAPI.getAll({ page: 1, page_size: 500 })
      .then(res => setProducts(Array.isArray(res) ? res : res.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));

    // Auto-fill next BOM number (server-side based on max existing BOM###)
    bomAPI.getNextNumber()
      .then(res => {
        if (res?.next_number) {
          setForm(f => f.bom_number ? f : { ...f, bom_number: res.next_number });
        }
      })
      .catch(() => { /* leave field blank — user can type manually */ });
  }, []);

  // Recalculate total FG cost whenever items or other costs change
  useEffect(() => {
    const itemsTotal = form.items.reduce((sum, i) => sum + (Number(i.total_cost) || 0), 0);
    const total = itemsTotal + (Number(form.child_bom_other_cost) || 0) + (Number(form.fg_other_cost) || 0);
    if (Math.abs(total - form.total_fg_cost) > 0.01) {
      setForm(f => ({ ...f, total_fg_cost: Math.round(total * 100) / 100 }));
    }
  }, [form.items, form.child_bom_other_cost, form.fg_other_cost]);

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      items[idx].total_cost = Math.round((Number(items[idx].quantity) || 0) * (Number(items[idx].rate) || 0) * 100) / 100;
    }
    setForm({ ...form, items });
  };

  const selectProduct = (idx, product) => {
    _applyBOMProduct(idx, product);
  };

  const handleBOMProductSelect = (idx, product) => {
    _applyBOMProduct(idx, product);
  };

  const _applyBOMProduct = (idx, product) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      item_id: product.item_code || product.sku || product.id,
      item_description: product.name,
      unit: product.unit || 'Kg',
      rate: product.cost_price || 0,
      total_cost: Math.round((Number(items[idx].quantity) || 0) * (product.cost_price || 0) * 100) / 100,
    };
    setForm({ ...form, items });
  };

  const handleBOMProductClear = (idx) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], item_id: '', item_description: '', unit: 'Kg', rate: 0, total_cost: 0 };
    setForm({ ...form, items });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { ...emptyItem, seq: String(form.items.length + 1) }] });
  };

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    const items = form.items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, seq: String(i + 1) }));
    setForm({ ...form, items });
  };

  const handleSubmit = async () => {
    if (!form.bom_number.trim()) { alert('Please enter a BOM number'); return; }
    if (!form.bom_name.trim()) { alert('Please enter a BOM name'); return; }
    if (!form.fg_item_id.trim()) { alert('Please enter a Finished Good item ID'); return; }
    if (form.items.some(i => !i.item_description)) { alert('Please fill all material rows'); return; }

    try {
      setSubmitting(true);
      await bomAPI.create(form);
      onCreated?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create BOM');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = prodSearch
    ? products.filter(p => (p.name || '').toLowerCase().includes(prodSearch.toLowerCase()) || (p.item_code || p.sku || '').toLowerCase().includes(prodSearch.toLowerCase()))
    : products;

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to BOM List
      </button>
      <h1 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>Create New BOM</h1>
      <p className="text-xs text-gray-500 mb-5">Define a bill of materials with raw materials and costs</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header Fields */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">BOM Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">BOM Number *</label>
                <input data-testid="bom-number-input" value={form.bom_number} onChange={e => setForm({ ...form, bom_number: e.target.value })} placeholder="Auto-generating..." className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                <p className="text-[9px] text-gray-400 mt-1">Auto-filled with next available number. Override if needed.</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">BOM Name *</label>
                <input data-testid="bom-name-input" value={form.bom_name} onChange={e => setForm({ ...form, bom_name: e.target.value })} placeholder="e.g. PRISMOLINE TYPE X" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">FG Item ID *</label>
                <input data-testid="fg-item-input" value={form.fg_item_id} onChange={e => setForm({ ...form, fg_item_id: e.target.value })} placeholder="e.g. TRM1101" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">FG UOM</label>
                <select data-testid="fg-uom-select" value={form.fg_uom} onChange={e => setForm({ ...form, fg_uom: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {['PCS', 'Kg', 'Sqft', 'Sqm', 'Mtr', 'Roll', 'Set', 'Ltr'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1">Remarks</label>
              <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Optional notes" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
          </div>

          {/* Materials Table */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Raw Materials ({form.items.length})</h3>
              <button onClick={addItem} data-testid="add-material-btn" className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
                <Plus className="w-3 h-3" /> Add Material
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 w-8">#</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 w-24">Item Code</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500">Description</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-20">Qty</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 w-16">Unit</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-20">Rate</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-24">Total</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-50" data-testid={`material-row-${idx}`}>
                      <td className="px-2 py-2 text-gray-400 font-mono">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <input value={item.item_id} onChange={e => updateItem(idx, 'item_id', e.target.value)} placeholder="Code" className="w-full px-2 py-1 text-xs border border-gray-200 rounded font-mono" />
                      </td>
                      <td className="px-2 py-2 relative">
                        <ProductSearchInput
                          value={item.item_description}
                          productId={item.item_id}
                          onSelect={prod => handleBOMProductSelect(idx, prod)}
                          onChange={val => updateItem(idx, 'item_description', val)}
                          onClear={() => handleBOMProductClear(idx)}
                          placeholder="Search product..."
                          testId={`bom-material-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" value={item.quantity || ''} onChange={e => updateItem(idx, 'quantity', Number(e.target.value))} className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-full px-1 py-1 text-xs border border-gray-200 rounded">
                          {['Kg', 'PCS', 'Sqft', 'Sqm', 'Mtr', 'Roll', 'Ltr', 'Set', 'Pkt', 'Drum'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" value={item.rate || ''} onChange={e => updateItem(idx, 'rate', Number(e.target.value))} className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-right" />
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-gray-800 font-mono">{(item.total_cost || 0).toFixed(2)}</td>
                      <td className="px-2 py-2">
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-400" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Cost Summary */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3 sticky top-5">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5" style={{ color: '#24549c' }} /> Cost Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Materials Cost</span>
                <span className="font-semibold text-gray-900">Rs. {form.items.reduce((s, i) => s + (Number(i.total_cost) || 0), 0).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">Child BOM Cost</span>
                <input type="number" step="0.01" value={form.child_bom_other_cost || ''} onChange={e => setForm({ ...form, child_bom_other_cost: Number(e.target.value) })} className="w-24 px-2 py-1 text-xs border border-gray-200 rounded text-right" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">FG Other Cost</span>
                <input type="number" step="0.01" value={form.fg_other_cost || ''} onChange={e => setForm({ ...form, fg_other_cost: Number(e.target.value) })} className="w-24 px-2 py-1 text-xs border border-gray-200 rounded text-right" />
              </div>
              <div className="border-t border-gray-100 pt-2 flex justify-between">
                <span className="text-sm font-bold text-gray-800">Total FG Cost</span>
                <span className="text-sm font-bold" style={{ color: '#24549c' }}>Rs. {(form.total_fg_cost || 0).toFixed(2)}</span>
              </div>
            </div>

            <button data-testid="create-bom-submit" onClick={handleSubmit} disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-60 mt-4"
              style={{ backgroundColor: '#24549c' }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {submitting ? 'Creating...' : 'Create BOM'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BOMCreateForm;
